// src/components/DeliveryTimeline.tsx
import React, { useEffect, useState, useMemo } from 'react';
import { Truck } from 'lucide-react';
import {
  estimateDeliveryForItem,
  combineEstimates,
  type DeliveryConfig,
  type Seller,
  type BuyerAddress,
} from '@/utils/deliveryUtils';
import { authFetch } from '@/services/api';

/* ---------------- DEFAULT CONFIG ---------------- */

const DEFAULT_CONFIG: DeliveryConfig = {
  enabled: true,
  useSellerAddress: true,
  defaultProcessingDays: 1,
  defaultTransitLocal: 1,
  defaultTransitRegional: 2,
  defaultTransitInterstate: 3,
  cutoffHour: 15, // 3 PM
  cutoffApplies: true,
  businessDaysOnly: true,
  cityOverrides: { Chennai: 1, Nellore: 3 },
  transitBufferDays: 1,
};

/* ---------------- HELPERS ---------------- */

const formatArrivesDate = (date: Date): string => {
  if (!date) return '';
  const weekday = date.toLocaleDateString('en-IN', { weekday: 'long' });
  const dayMonth = date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
  });
  return `${weekday}, ${dayMonth}`;
};

/* ---------------- COMPONENT ---------------- */

interface DeliveryTimelineProps {
  items?: Array<{ seller?: Seller }>;
  seller?: Seller | null;
  buyerAddress?: BuyerAddress | null;
  short?: boolean;
  shipping?: { isFree: boolean; cost: number | null };
  className?: string;
}

export default function DeliveryTimeline({
  items = [],
  seller = null,
  buyerAddress = null,
  short = false,
  shipping = { isFree: false, cost: null },
  className = '',
}: DeliveryTimelineProps) {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<DeliveryConfig>(DEFAULT_CONFIG);
  const [estimate, setEstimate] = useState<ReturnType<typeof combineEstimates>>(null);

  /* ---------- Load delivery config ---------- */
  useEffect(() => {
    let mounted = true;

    const loadConfig = async () => {
      try {
        const response = await authFetch('/api/config/delivery');
        if (!response.ok) throw new Error('Config fetch failed');
        
        const data = await response.json();
        if (!mounted) return;

        setConfig({
          enabled: data?.enabled ?? true,
          useSellerAddress: data?.useSellerAddress ?? true,
          defaultProcessingDays: Number(data?.defaultProcessingDays ?? 1),
          defaultTransitLocal: Number(data?.defaultTransitLocal ?? 1),
          defaultTransitRegional: Number(data?.defaultTransitRegional ?? 2),
          defaultTransitInterstate: Number(data?.defaultTransitInterstate ?? 3),
          cutoffHour: Number(data?.cutoffHour ?? 15),
          cutoffApplies: data?.cutoffApplies ?? true,
          businessDaysOnly: data?.businessDaysOnly ?? true,
          cityOverrides: data?.cityOverrides ?? DEFAULT_CONFIG.cityOverrides,
          transitBufferDays: Number(data?.transitBufferDays ?? 1),
        });
      } catch {
        if (mounted) setConfig(DEFAULT_CONFIG);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadConfig();
    return () => {
      mounted = false;
    };
  }, []);

  /* ---------- Estimate delivery ---------- */
  const inputKey = useMemo(
    () => JSON.stringify({ items, seller, buyerAddress, config }),
    [items, seller, buyerAddress, config]
  );

  useEffect(() => {
    if (loading || !config.enabled) return;

    const list =
      Array.isArray(items) && items.length
        ? items
        : seller
        ? [{ seller }]
        : [];

    const estimates = list
      .filter((it) => it?.seller)
      .map((it) =>
        estimateDeliveryForItem({
          seller: it.seller!,
          buyerAddress,
          config,
          now: new Date(),
        })
      )
      .filter(Boolean);

    setEstimate(estimates.length ? combineEstimates(estimates) : null);
  }, [loading, inputKey]);

  /* ---------- Loading ---------- */
  if (loading) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div className="animate-pulse h-4 w-4 bg-muted rounded" />
        <div className="animate-pulse h-4 w-32 bg-muted rounded" />
      </div>
    );
  }

  if (!config.enabled || !estimate) return null;

  /* ---------- Pick ONE clear arrival date ---------- */
  let arrivalDate: Date | null = null;

  if (estimate.earliest && estimate.latest) {
    // Always promise safest date
    arrivalDate = estimate.latest;
  } else if (estimate.maxDays != null) {
    const now = new Date();
    if (config.businessDaysOnly) {
      // Simple approximation: add business days by skipping weekends
      let currentDate = new Date(now);
      let daysAdded = 0;
      while (daysAdded < estimate.maxDays) {
        currentDate.setDate(currentDate.getDate() + 1);
        const dayOfWeek = currentDate.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Not Sunday or Saturday
          daysAdded++;
        }
      }
      arrivalDate = currentDate;
    } else {
      arrivalDate = new Date(now.getTime() + estimate.maxDays * 24 * 60 * 60 * 1000);
    }
  }

  if (!arrivalDate) return null;

  /* ---------- Urgency logic (cutoff based) ---------- */
  const urgency = (() => {
    if (!config.cutoffApplies || !estimate.earliest) return null;

    const now = new Date();
    const cutoff = new Date();
    cutoff.setHours(config.cutoffHour || 15, 0, 0, 0);

    if (now >= cutoff) return null;

    const diffMs = cutoff.getTime() - now.getTime();
    const mins = Math.floor(diffMs / (1000 * 60));

    // Show urgency only if within next 3 hours
    if (mins <= 0 || mins > 180) return null;

    return `Order within ${mins} min for dispatch today`;
  })();

  /* ---------- UI ---------- */
  return (
    <div className={`py-1.5 ${className}`}>
      <div className="flex items-center gap-2 text-sm font-bold text-foreground">
        <Truck className="h-4 w-4" />
        <span>Arrives {formatArrivesDate(arrivalDate)}</span>
        {shipping?.isFree && (
          <span className="text-green-600 font-bold">• FREE Delivery</span>
        )}
        {!shipping?.isFree && shipping?.cost != null && (
          <span className="text-muted-foreground font-semibold">• Shipping ₹{shipping.cost}</span>
        )}
      </div>

      {urgency && (
        <div className="mt-1 text-xs text-red-600 font-semibold">
          {urgency}
        </div>
      )}
    </div>
  );
}

export { DeliveryTimeline };